'use client';

import { ConfigStore, createConfigStore } from '@/stores/config-store';
import { bindConfigStore } from '@common/application/context/Providers';
import { StorageKey } from '@common/application/enums/StorageKey';
import { Nullable } from '@common/types/utils';
import { useSession } from 'next-auth/react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { useStore } from 'zustand/react';

export type ConfigStoreApi = ReturnType<typeof createConfigStore>;

export const ConfigStoreContext = createContext<ConfigStoreApi | undefined>(undefined);

export interface ConfigStoreProviderProps {
  children: React.ReactNode;
}

function ConfigStoreProvider(props: ConfigStoreProviderProps) {
  const [impersonatedRole, setImpersonatedRole] = useSessionStorage(StorageKey.IMPERSONATED_ROLE, null);
  const [company, setCompany] = useSessionStorage(StorageKey.COMPANY, null);

  const storeRef = useRef<Nullable<ConfigStoreApi>>(null);

  if (!storeRef.current) {
    storeRef.current = createConfigStore();
    bindConfigStore(storeRef.current);
  }

  const { data: session } = useSession();

  useEffect(() => {
    if (!session) return;
    const state = storeRef.current!.getState();

    if (!state.company) {
      state.setCompany(company ?? session.user.defaultCompanyId);
    }
    if (!state.role) {
      state.setRole(session.user.defaultRole);
    }
    if (!state.impersonatedRole && impersonatedRole) {
      state.setImpersonatedRole(impersonatedRole);
    }
  }, [company, impersonatedRole, session]);

  return <ConfigStoreContext.Provider value={storeRef.current}>{props.children}</ConfigStoreContext.Provider>;
}

export const useConfigStore = <T,>(selector: (store: ConfigStore) => T): T => {
  const configStoreContext = useContext(ConfigStoreContext);

  if (!configStoreContext) {
    throw new Error(`useConfigStore must be used within ConfigStoreProvider`);
  }

  return useStore(configStoreContext, selector);
};

export default ConfigStoreProvider;
